// src/index.tsx
import React, { useEffect as useEffect2, useState as useState2, useMemo, useCallback, useRef, createContext, useContext } from "react";
import { PublicKey as PublicKey3, Transaction, VersionedTransaction } from "@solana/web3.js";

// src/error.tsx
var Errors = {
  INITIALIZE_ERROR: "INITIALIZE_ERROR",
  FETCH_ROUTES_ERROR: "FETCH_ROUTES_ERROR",
  NO_ROUTES_ERROR: "NO_ROUTES_ERROR",
  TOKEN_NOT_TRADABLE: "TOKEN_NOT_TRADABLE"
};

// src/utils/useDebounce.ts
import { useEffect, useState } from "react";
function useDebounce(value, delay) {
  const [debouncedValue, setDebouncedValue] = useState(value);
  useEffect(
    () => {
      const handler = setTimeout(() => {
        setDebouncedValue(value);
      }, delay);
      return () => {
        clearTimeout(handler);
      };
    },
    [value, delay]
  );
  return debouncedValue;
}

// src/index.tsx
import JSBI2 from "jsbi";
import { TOKEN_PROGRAM_ID as TOKEN_PROGRAM_ID2 } from "@solana/spl-token";

// src/utils/api.ts
import { array, coerce, instance, integer, number, string, type, enums, optional } from "superstruct";
import { PublicKey } from "@solana/web3.js";
import JSBI from "jsbi";
var PublicKeyFromString = coerce(instance(PublicKey), string(), (value) => new PublicKey(value));
var AmountFromString = coerce(instance(JSBI), string(), (value) => JSBI.BigInt(value));
var NumberFromString = coerce(string(), number(), (value) => new Number(value));
var SwapInfo = type({
  ammKey: PublicKeyFromString,
  label: string(),
  inputMint: string(),
  outputMint: string(),
  inAmount: AmountFromString,
  outAmount: AmountFromString,
  feeAmount: AmountFromString,
  feeMint: PublicKeyFromString
});
var RoutePlanStep = type({
  swapInfo: SwapInfo,
  percent: integer()
});
var RoutePlanWithMetadata = array(RoutePlanStep);
var QuoteResponse = type({
  inputMint: PublicKeyFromString,
  inAmount: AmountFromString,
  outputMint: PublicKeyFromString,
  outAmount: AmountFromString,
  otherAmountThreshold: AmountFromString,
  priceImpactPct: NumberFromString,
  swapMode: enums(["ExactIn", "ExactOut"]),
  routePlan: RoutePlanWithMetadata,
  slippageBps: number(),
  contextSlot: number(),
  computedAutoSlippage: optional(number())
});

// src/index.tsx
import { create } from "superstruct";
import { createJupiterApiClient } from "@jup-ag/api";
import { TransactionError, checkTxSize } from "@mercurial-finance/optimist";
import {
  SwapMode,
  Owner,
  executeTransaction,
  JUPITER_V6_ERRORS,
  MARKETS_URL,
  TOKEN_LIST_URL,
  JUPITER_FEE_OWNER,
  LAMPORTS_PER_SIGNATURE,
  calculateTransactionDepositAndFeeV2,
  getTransactionBlockhash,
  getReferralTokenAccount
} from "@jup-ag/common";
import { NATIVE_MINT as WRAPPED_SOL_MINT } from "@solana/spl-token";

// src/utils/ata.ts
import { ASSOCIATED_TOKEN_PROGRAM_ID, TOKEN_PROGRAM_ID } from "@solana/spl-token";
import { PublicKey as PublicKey2 } from "@solana/web3.js";
function getAssociatedTokenAddressSync(mint, owner, tokenProgramId = TOKEN_PROGRAM_ID) {
  const [ata] = PublicKey2.findProgramAddressSync(
    [owner.toBuffer(), tokenProgramId.toBuffer(), mint.toBuffer()],
    ASSOCIATED_TOKEN_PROGRAM_ID
  );
  return ata;
}

// src/debug.ts
function getQuoteParamsOverrides() {
  const overrides = {};
  const quoteParamsJson = localStorage.getItem("quoteParams");
  if (quoteParamsJson) {
    const quoteArgs = JSON.parse(quoteParamsJson);
    Object.assign(overrides, quoteArgs);
  }
  const quoteArgsJson = localStorage.getItem("quoteArgs");
  if (quoteArgsJson) {
    const quoteArgs = JSON.parse(quoteArgsJson);
    for (const [name, value] of Object.entries(quoteArgs)) {
      overrides[`quoteArg-${name}`] = value;
    }
  }
  return overrides;
}

// src/index.tsx
import promiseRetry from "promise-retry";
import { JUPITER_PROGRAM_V4_ID, JUPITER_PROGRAM_V6_ID, OPENBOOK_PROGRAM_ID } from "@jup-ag/common";
var JupiterContext = createContext(null);
var JupiterProvider = ({
  onlyDirectRoutes,
  userPublicKey,
  jupiterQuoteApiUrl,
  children,
  ...jupiterLoadProps
}) => {
  const [programIdToLabelMap, setProgramIdToLabelMap] = useState2(/* @__PURE__ */ new Map());
  const [error, setError] = useState2();
  const [programIdsExcluded, setProgramIdsExcluded] = useState2(/* @__PURE__ */ new Set());
  const platformFeeAndAccountsRef = useRef();
  useEffect2(() => {
    platformFeeAndAccountsRef.current = jupiterLoadProps.platformFeeAndAccounts;
  }, [jupiterLoadProps.platformFeeAndAccounts]);
  const setPlatformFeeAccount = useCallback((mint, platformFeeAccount) => {
    if (!platformFeeAndAccountsRef.current)
      return;
    if (!platformFeeAndAccountsRef.current.feeAccounts)
      platformFeeAndAccountsRef.current.feeAccounts = /* @__PURE__ */ new Map();
    platformFeeAndAccountsRef.current.feeAccounts.set(mint, platformFeeAccount);
  }, []);
  const getPlatformFeeAccount = useCallback(async (platformFeeMint) => {
    var _a;
    const platformFeeAndAccounts = platformFeeAndAccountsRef.current;
    const platformFeeAccount = (_a = platformFeeAndAccounts == null ? void 0 : platformFeeAndAccounts.feeAccounts) == null ? void 0 : _a.get(platformFeeMint);
    if (platformFeeAccount)
      return platformFeeAccount;
    if (platformFeeAccount === null)
      return;
    if (platformFeeAndAccounts == null ? void 0 : platformFeeAndAccounts.referralAccount) {
      const referralTokenAccount = await getReferralTokenAccount({
        connection: jupiterLoadProps.connection,
        referralAccount: platformFeeAndAccounts.referralAccount,
        mint: new PublicKey3(platformFeeMint)
      });
      setPlatformFeeAccount(platformFeeMint, referralTokenAccount);
      if (referralTokenAccount)
        return referralTokenAccount;
    }
    return;
  }, []);
  const jupiterApiClient = useMemo(() => {
    if (jupiterQuoteApiUrl) {
      return createJupiterApiClient({
        basePath: jupiterQuoteApiUrl
      });
    }
    return createJupiterApiClient();
  }, [jupiterQuoteApiUrl]);
  useEffect2(() => {
    setProgramIdsExcluded((set) => {
      if (set.size) {
        return /* @__PURE__ */ new Set();
      }
      return set;
    });
  }, [jupiterLoadProps.asLegacyTransaction]);
  useEffect2(() => {
    async function fetchProgramIdToLabelMap() {
      const programIdToLabel = await jupiterApiClient.programIdToLabelGet();
      const programIdToLabelMap2 = new Map(Object.entries(programIdToLabel));
      setProgramIdToLabelMap(programIdToLabelMap2);
    }
    fetchProgramIdToLabelMap();
  }, [jupiterApiClient]);
  return /* @__PURE__ */ React.createElement(
    JupiterContext.Provider,
    {
      value: {
        ...jupiterLoadProps,
        getPlatformFeeAccount,
        userPublicKey,
        programIdToLabelMap,
        jupiterApiClient,
        error,
        setError,
        onlyDirectRoutes,
        programIdsExcluded,
        setProgramIdsExcluded
      }
    },
    children
  );
};
var useJupiterClient = () => {
  const context = useContext(JupiterContext);
  if (!context) {
    throw new Error("JupiterProvider is required");
  }
  return context.jupiterApiClient;
};
var useJupiter = ({
  amount,
  inputMint,
  outputMint,
  autoSlippage,
  maxAutoSlippageBps,
  autoSlippageCollisionUsdValue,
  slippageBps,
  swapMode,
  debounceTime = 250,
  maxAccounts,
  disabledProgramIds,
  computeAutoSlippage
}) => {
  const context = useContext(JupiterContext);
  const [loading, setLoading] = useState2(false);
  const [quoteResponseMeta, setQuoteResponseMeta] = useState2();
  const [refreshCount, setRefreshCount] = useState2(0);
  const lastRefreshCount = useRef(refreshCount);
  const [debouncedAmount, debouncedInputMint, debouncedOutputMint, debouncedSwapMode] = useDebounce(
    React.useMemo(() => {
      if (JSBI2.greaterThan(amount, JSBI2.BigInt(0))) {
        setLoading(true);
      }
      return [amount, inputMint, outputMint, swapMode];
    }, [amount.toString(), inputMint == null ? void 0 : inputMint.toBase58(), outputMint == null ? void 0 : outputMint.toBase58(), swapMode]),
    debounceTime
  );
  const lastRefreshTimestamp = useRef(0);
  const lastQueryTimestamp = useRef(0);
  if (!context) {
    throw new Error("JupiterProvider is required");
  }
  const {
    error,
    setError,
    routeCacheDuration = 0,
    onlyDirectRoutes,
    jupiterApiClient,
    userPublicKey,
    wrapUnwrapSOL = true,
    asLegacyTransaction,
    platformFeeAndAccounts,
    getPlatformFeeAccount,
    programIdToLabelMap,
    setProgramIdsExcluded,
    programIdsExcluded
  } = context;
  useEffect2(() => {
    lastRefreshCount.current = -1;
  }, [
    [debouncedInputMint == null ? void 0 : debouncedInputMint.toString(), debouncedOutputMint == null ? void 0 : debouncedOutputMint.toString()].sort().join("-"),
    autoSlippage,
    maxAutoSlippageBps,
    autoSlippageCollisionUsdValue,
    slippageBps
  ]);
  useEffect2(() => {
    if (lastRefreshTimestamp.current && new Date().getTime() - lastRefreshTimestamp.current >= routeCacheDuration) {
      lastRefreshCount.current = -1;
    }
    if (JSBI2.greaterThan(debouncedAmount, JSBI2.BigInt(0))) {
      setLoading(true);
    } else {
      setLoading(false);
    }
  }, [
    refreshCount,
    debouncedAmount,
    slippageBps,
    debouncedInputMint,
    debouncedOutputMint,
    onlyDirectRoutes,
    asLegacyTransaction
  ]);
  const fetchQuote = useCallback(async () => {
    try {
      if (JSBI2.equal(debouncedAmount, JSBI2.BigInt(0)) || error === Errors.INITIALIZE_ERROR) {
        return null;
      } else if (debouncedAmount) {
        if (!debouncedInputMint || !debouncedOutputMint)
          return null;
        if (slippageBps && autoSlippage)
          throw new Error("Cannot set both slippageBps and autoSlippage");
        if (!autoSlippage && maxAutoSlippageBps)
          throw new Error("Cannot set maxAutoSlippageBps without autoSlippage");
        const inputMint2 = debouncedInputMint.toBase58();
        const outputMint2 = debouncedOutputMint.toBase58();
        if (swapMode === SwapMode.ExactOut && maxAccounts) {
          console.warn("maxAccounts is not supported for ExactOut swap mode");
        }
        const platformFeeMint = swapMode === SwapMode.ExactOut ? inputMint2 : outputMint2;
        let quoteResponse;
        let original;
        const excludeDexes = Array.from([...programIdsExcluded, ...disabledProgramIds || []]).map((id) => programIdToLabelMap.get(id)).filter((id) => Boolean(id));
        const quoteParamsOverrides = getQuoteParamsOverrides();
        const apiClient = new Proxy(jupiterApiClient, {
          get: function(target, prop) {
            if (prop === "request") {
              return (context2, initOverrides) => {
                if (Object.keys(quoteParamsOverrides).length) {
                  console.log("Quoting with overrides:", quoteParamsOverrides);
                  Object.assign(context2.query, quoteParamsOverrides);
                }
                if (wrapUnwrapSOL && excludeDexes.includes("Jupiter LO") === false) {
                  Object.assign(context2.query, {
                    experimentalDexes: "Jupiter LO"
                  });
                }
                return target.request(context2, initOverrides);
              };
            }
            return Reflect.get(target, prop);
          }
        });
        const platformFeeBps = await getPlatformFeeAccount(platformFeeMint) ? platformFeeAndAccounts == null ? void 0 : platformFeeAndAccounts.feeBps : void 0;
        const response = await promiseRetry(
          (retry) => apiClient.quoteGet({
            inputMint: inputMint2,
            outputMint: outputMint2,
            amount: debouncedAmount.toString(),
            autoSlippage,
            maxAutoSlippageBps,
            autoSlippageCollisionUsdValue,
            slippageBps,
            swapMode,
            excludeDexes: excludeDexes.length ? excludeDexes : void 0,
            onlyDirectRoutes,
            asLegacyTransaction,
            platformFeeBps,
            maxAccounts,
            computeAutoSlippage
          }).catch((e) => {
            retry(e);
          }),
          {
            retries: 1,
            minTimeout: 100
          }
        );
        original = Object.assign({}, response);
        quoteResponse = create(response, QuoteResponse);
        return { quoteResponse, original, timestamp: new Date().getTime() };
      }
      return null;
    } catch (error2) {
      throw error2;
    }
  }, [
    debouncedAmount,
    debouncedInputMint,
    debouncedOutputMint,
    debouncedSwapMode,
    slippageBps,
    maxAutoSlippageBps,
    swapMode,
    maxAccounts,
    userPublicKey,
    wrapUnwrapSOL,
    refreshCount,
    onlyDirectRoutes,
    asLegacyTransaction,
    programIdsExcluded
  ]);
  useEffect2(() => {
    let requestAborted = false;
    (async () => {
      if (JSBI2.equal(debouncedAmount, JSBI2.BigInt(0)) || error === Errors.INITIALIZE_ERROR) {
        setLoading(false);
        setQuoteResponseMeta(void 0);
      } else if (debouncedAmount) {
        if (!debouncedInputMint || !debouncedOutputMint)
          return;
        const lastUpdatedTime = new Date().getTime();
        lastQueryTimestamp.current = lastUpdatedTime;
        lastRefreshTimestamp.current = lastUpdatedTime;
        try {
          const fetchedQuote = await fetchQuote();
          if (!(fetchedQuote == null ? void 0 : fetchedQuote.quoteResponse) || !(fetchedQuote == null ? void 0 : fetchedQuote.original) || !(fetchedQuote == null ? void 0 : fetchedQuote.timestamp) || requestAborted)
            return;
          setQuoteResponseMeta({
            quoteResponse: fetchedQuote.quoteResponse,
            original: fetchedQuote.original
          });
          setError(void 0);
        } catch (e) {
          console.error(e);
          if (lastQueryTimestamp.current !== lastUpdatedTime) {
            return;
          }
          if ("response" in e) {
            const errorObj = await e.response.json();
            if ((errorObj == null ? void 0 : errorObj.error) === "No routes found") {
              setError(Errors.NO_ROUTES_ERROR);
            } else if ((errorObj == null ? void 0 : errorObj.errorCode) === Errors.TOKEN_NOT_TRADABLE) {
              setError(Errors.TOKEN_NOT_TRADABLE);
            }
          } else {
            setError(Errors.FETCH_ROUTES_ERROR);
          }
          setQuoteResponseMeta(void 0);
        } finally {
          if (lastQueryTimestamp.current !== lastUpdatedTime) {
            return;
          }
          lastRefreshCount.current = refreshCount;
          setLoading(false);
        }
      }
    })();
    return () => {
      requestAborted = true;
    };
  }, [fetchQuote]);
  const { exchange, fetchSwapTransaction } = useJupiterExchange(swapMode);
  return {
    exchange,
    fetchQuote,
    fetchSwapTransaction,
    refresh: React.useCallback(() => {
      if (!loading && lastRefreshTimestamp.current) {
        setRefreshCount((refreshCount2) => refreshCount2 + 1);
      }
    }, [loading]),
    lastRefreshTimestamp: lastRefreshTimestamp.current,
    loading,
    quoteResponseMeta,
    error,
    programIdToLabelMap,
    setProgramIdsExcluded,
    programIdsExcluded
  };
};
var useJupiterExchange = (swapMode) => {
  const context = useContext(JupiterContext);
  if (!context) {
    throw new Error("JupiterProvider is required");
  }
  const {
    jupiterApiClient,
    userPublicKey,
    connection,
    wrapUnwrapSOL = true,
    asLegacyTransaction,
    platformFeeAndAccounts,
    useSharedAccounts,
    getPlatformFeeAccount
  } = context;
  const fetchSwapTransaction = useCallback(
    async ({
      quoteResponseMeta,
      userPublicKey: userPublicKey2,
      wrapUnwrapSOL: propsWrapUnwrapSOL,
      prioritizationFeeLamports
    }) => {
      if (!userPublicKey2)
        throw new Error("User public key is required");
      const wrapAndUnwrapSol = Boolean(propsWrapUnwrapSOL ?? wrapUnwrapSOL ?? true);
      const { inputMint, outputMint } = quoteResponseMeta.quoteResponse;
      const platformFeeMint = swapMode === SwapMode.ExactOut ? inputMint : outputMint;
      const feeAccount = await getPlatformFeeAccount(platformFeeMint.toBase58());
      const result = await promiseRetry(
        (retry) => jupiterApiClient.swapPost({
          swapRequest: {
            quoteResponse: quoteResponseMeta.original,
            userPublicKey: userPublicKey2.toBase58(),
            wrapAndUnwrapSol,
            feeAccount: feeAccount == null ? void 0 : feeAccount.toBase58(),
            prioritizationFeeLamports,
            asLegacyTransaction,
            useSharedAccounts,
            dynamicComputeUnitLimit: true
          }
        }).catch(async (res) => {
          try {
            const { error } = await res.json();
            return { error: new TransactionError(error) };
          } catch (e) {
            return retry({
              error: new TransactionError("Unknown error")
            });
          }
        }),
        {
          retries: 1,
          minTimeout: 100
        }
      );
      if ("error" in result) {
        return result;
      }
      const { swapTransaction: swapTransactionSerialized, lastValidBlockHeight } = result;
      const swapTransactionBuffer = Buffer.from(swapTransactionSerialized, "base64");
      const swapTransaction = asLegacyTransaction ? Transaction.from(swapTransactionBuffer) : VersionedTransaction.deserialize(swapTransactionBuffer);
      checkTxSize(swapTransaction);
      return {
        swapTransaction,
        blockhash: getTransactionBlockhash(swapTransaction),
        lastValidBlockHeight
      };
    },
    [wrapUnwrapSOL, asLegacyTransaction, useSharedAccounts, getPlatformFeeAccount, jupiterApiClient]
  );
  const exchange = useCallback(
    async ({
      wallet,
      quoteResponseMeta,
      onTransaction,
      prioritizationFeeLamports,
      ...restExchangeProps
    }) => {
      if (!userPublicKey)
        throw new Error("User public key is required");
      if (!wallet)
        throw new Error("Wallet is required");
      const { inputMint, outputMint } = quoteResponseMeta.quoteResponse;
      const wrapAndUnwrapSol = Boolean(restExchangeProps.wrapUnwrapSOL ?? wrapUnwrapSOL ?? true);
      const [result, accountInfos] = await Promise.all([
        fetchSwapTransaction({
          quoteResponseMeta,
          userPublicKey,
          wrapUnwrapSOL: wrapAndUnwrapSol,
          prioritizationFeeLamports,
          ...restExchangeProps
        }),
        connection.getMultipleAccountsInfo([inputMint, outputMint])
      ]);
      if ("error" in result) {
        return result;
      }
      const { blockhash, lastValidBlockHeight, swapTransaction } = result;
      const [sourceAddress, destinationAddress] = [inputMint, outputMint].map(
        (mint, idx) => {
          var _a;
          return getAssociatedTokenAddressSync(mint, userPublicKey, ((_a = accountInfos[idx]) == null ? void 0 : _a.owner) || TOKEN_PROGRAM_ID2);
        }
      );
      return executeTransaction({
        connection,
        wallet,
        onTransaction,
        inputMint,
        outputMint,
        sourceAddress,
        destinationAddress,
        swapTransaction,
        blockhashWithExpiryBlockHeight: { lastValidBlockHeight, blockhash },
        owner: new Owner(userPublicKey),
        wrapUnwrapSOL: wrapAndUnwrapSol
      });
    },
    [userPublicKey, wrapUnwrapSOL, asLegacyTransaction, connection, platformFeeAndAccounts, jupiterApiClient, swapMode]
  );
  return { exchange, fetchSwapTransaction };
};
var calculateFeeForSwap = (quoteResponse, mintToAtaMap, openbookMarketToOpenOrderMap, wrapUnwrapSOL, useSharedAccounts) => {
  const atasToBeCreated = /* @__PURE__ */ new Set();
  const openOrdersToBeCreated = /* @__PURE__ */ new Set();
  const isInputMintSOL = Boolean(wrapUnwrapSOL && (quoteResponse == null ? void 0 : quoteResponse.inputMint.equals(WRAPPED_SOL_MINT)));
  const walkRoutePlan = (routePlan) => {
    if (!useSharedAccounts) {
      routePlan.forEach((routeStep) => {
        if (!mintToAtaMap.has(routeStep.swapInfo.outputMint)) {
          atasToBeCreated.add(routeStep.swapInfo.outputMint);
        }
        if (routeStep.swapInfo.label === "Openbook") {
          if (!openbookMarketToOpenOrderMap.has(routeStep.swapInfo.ammKey.toBase58())) {
            openOrdersToBeCreated.add(routeStep.swapInfo.outputMint);
          }
        }
      });
    }
  };
  if (quoteResponse) {
    if (useSharedAccounts) {
      let numOfAtasToBeCreated = 0;
      if (quoteResponse.inputMint.equals(WRAPPED_SOL_MINT) || quoteResponse.outputMint.equals(WRAPPED_SOL_MINT) && wrapUnwrapSOL && !mintToAtaMap.has(WRAPPED_SOL_MINT.toBase58())) {
        numOfAtasToBeCreated = 1;
      }
      return calculateTransactionDepositAndFeeV2({
        numOfAtasToBeCreated,
        numOfOpenOrdersToBeCreated: 0,
        isInputMintSOL
      });
    }
    if (quoteResponse.inputMint.equals(WRAPPED_SOL_MINT) && wrapUnwrapSOL && !mintToAtaMap.has(WRAPPED_SOL_MINT.toBase58())) {
      atasToBeCreated.add(WRAPPED_SOL_MINT.toBase58());
    }
    walkRoutePlan(quoteResponse.routePlan);
  }
  return calculateTransactionDepositAndFeeV2({
    numOfAtasToBeCreated: atasToBeCreated.size,
    numOfOpenOrdersToBeCreated: openOrdersToBeCreated.size,
    isInputMintSOL
  });
};
export {
  Errors,
  JUPITER_FEE_OWNER,
  JUPITER_PROGRAM_V4_ID,
  JUPITER_PROGRAM_V6_ID,
  JUPITER_V6_ERRORS,
  JupiterProvider,
  LAMPORTS_PER_SIGNATURE,
  MARKETS_URL,
  OPENBOOK_PROGRAM_ID,
  Owner,
  SwapMode,
  TOKEN_LIST_URL,
  calculateFeeForSwap,
  useJupiter,
  useJupiterClient,
  useJupiterExchange
};
//# sourceMappingURL=index.mjs.map